import React, {useCallback, useMemo, useState} from 'react';
import {FlexLayout} from '@wix/devzai-common-client';
import {classes, style} from './bymo-template-speed-dial.st.css';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {
    BymoTemplateMetadataInferBymoPageProps,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetValue,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible
} from '../../../client-server-common';
import {DoppePageBackgroundView} from '../../doppe-page-background-view/doppe-page-background-view';
import {DoppeViewerCoverView} from '../../doppe-viewer-cover-view/doppe-viewer-cover-view';
import {
    BymoTemplateSpeedDialActionsListAppearance,
    BymoTemplateSpeedDialAutoComplete,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialTemplateMetadata
} from './bymo-template-speed-dial.metadata';
import {
    doppeDtoActionGetDefaultProps,
    DoppeDtoActionViewType
} from '../../../client-server-common/types/doppe-dto-action';
import {
    doppeActionTypeSpeedDial
} from '../../../doppe-action-types/doppe-action-type-speed-dial/doppe-action-type-speed-dial';
import {DoppeViewerActionsList} from '../../doppe-viewer-actions-list/doppe-viewer-actions-list';
import {DoppeTemplateBasedActionView} from '../../doppe-template-based-action-view/doppe-template-based-action-view';
import {
    DoppeActionViewInnerContentCloseButton
} from '../../doppe-action-view-inner-content-close-button/doppe-action-view-inner-content-close-button';
import {
    arrayIsNotNullableOrEmpty,
    evaluateFunction,
    iterableAny,
    ObservableSet,
    stringIsNotNullableOrWhiteSpace,
    textNormalizeForHtmlDisplay
} from '@wix/devzai-utils-common';
import {DoppePageFooterBanner} from '../../doppe-page-footer-banner/doppe-page-footer-banner';
import {DoppeViewerAction} from '../../doppe-viewer-action/doppe-viewer-action';
import {DoppeViewerActionContext} from '../../doppe-viewer-action-context/doppe-viewer-action-context';
import {
    DelayedComponentRenderer, DomElementAnimator,
    ExpandableText,
    FloatingWindowOpener,
    InnerWindowAnimator,
    MultilineExpandableText,
    useObservable
} from '@wix/devzai-utils-react';
import {useDoppeViewerLangDictionary} from '../../doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {DoppePageSocialLinks} from '../../doppe-page-social-links/doppe-page-social-links';
import {bymoPageSocialLinkIsHidden, DoppeProfileNameAutoSource, PreRollExperience} from '../../../client-server-common';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {DoppePageProfileNameView} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {
    DoppeViewerDefaultNotificationContent
} from '../../doppe-viewer-default-notification-content/doppe-viewer-default-notification-content';
import {
    useDoppeViewerAppUiServiceComponentRef
} from '../../../client/doppe-viewer-app-client-services/doppe-viewer-app-ui-service';
import {
    DoppLayoutContentTitle
} from '../../doppe-profile-view-templates/dopp-layouts-components/dopp-layout-content-title/dopp-layout-content-title';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../../bymo-page-viewer-context/bymo-page-viewer-context';
import {BymoPageLayout, useBymoPageLayout} from '../../bymo-page-style/bymo-page-style';
import {
    DoppeViewerAutoRedirectNotificationContent
} from '../../doppe-viewer-auto-redirect-notification-content/doppe-viewer-auto-redirect-notification-content';
import {BymoSkipHeader, BymoSkipHeaderSkin} from '../../bymo-skip-header/bymo-skip-header';
import {DoppePageCookieBannerOpener} from "../../doppe-page-cookie-consent/doppe-page-cookie-banner-opener/doppe-page-cookie-banner-opener";

export const BymoTemplateSpeedDialInner = React.memo(function BymoTemplateSpeedDialInner(
    props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>
) {
    const {
        // autoCompleteBehaviour,
        // showFullScreenAutoCompleteOnMobile,
        actionButtonTemplate,
        actions,
        actionsHeading,
        pageTitle,
        actionsListAppearance,
        socialLinks,
        searchAppearance,
        profilePictureLinkInBio,
        bioTextMaxLines,
        removeHoppBanner,

        coverPhoto,
        callToActionText,
        bioText,
        searchPlaceholder,
        preRollExperience
    } = props;

    const viewerLang = useDoppeViewerLangDictionary();
    const contextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();

    const normalizedPreRollExperience = preRollExperience === PreRollExperience.Auto ?
        PreRollExperience.FloatingHeader :
        preRollExperience;

    const targetUrl = contextProps.targetUrl;

    const actionsHeadingVisibleValue = doppeHideableValueGetVisibleValue(actionsHeading, '');
    const bioTextVisibleValue = doppeHideableValueGetVisibleValue(bioText, '');

    const hasCover = useMemo(() => {
        return doppeHideableValueIsVisible(coverPhoto) && doppeHideableValueGetValue(coverPhoto) !== null && doppeHideableValueGetValue(coverPhoto) !== undefined;
    }, [coverPhoto]);

    const actionViewRenderFunction = useCallback<DoppeViewerAction.ActionViewRenderFunction>(renderProps => {
        return (
            <DoppeTemplateBasedActionView
                {...renderProps.actionViewContextProps}
                actionViewTemplate={actionButtonTemplate}
                renderInlineContentCloseButton={renderProps => {
                    return (
                        <DoppeActionViewInnerContentCloseButton
                            onCloseButtonClick={renderProps.closingFunction}
                        />
                    )
                }}
            />
        )
    }, [actionButtonTemplate])

    const fullScreenToggledActionsSet = useObservable(useMemo(() => new ObservableSet(), []));

    const fullScreenModeTogglingController = useMemo<DoppeViewerActionContext.FullScreenModeTogglingController>(() => {
        return {
            toggleFullScreenMode(actionId, isFullScreen) {
                fullScreenToggledActionsSet.toggleValue(actionId, isFullScreen);
            }
        }
    }, []);

    const autoRedirectNotificationOpenerRef = useDoppeViewerAppUiServiceComponentRef('autoRedirectNotificationOpener');
    const defaultNotificationOpenerRef = useDoppeViewerAppUiServiceComponentRef('defaultNotificationOpener');
    const [_defaultNotificationIsShown, setDefaultNotificationIsShown] = useState(false);

    const showAutoRedirectNotificationInTheBottom = true;
    const [autoRedirectNotificationIsShown, setAutoRedirectNotificationIsShown] = useState(false);

    const bymoPageLayout = useBymoPageLayout();

    const skipHeaderIsFloating =
        normalizedPreRollExperience === PreRollExperience.FloatingHeader ||
        bymoPageLayout === BymoPageLayout.Wide;


    const visibleSocialLinks = socialLinks.filter(socialLink => !bymoPageSocialLinkIsHidden(socialLink));

    const searchSectionIsVisible = searchAppearance !== BymoTemplateSpeedDialSearchAppearance.Hidden;

    const showProfileSeparator =
        (stringIsNotNullableOrWhiteSpace(bioTextVisibleValue) || arrayIsNotNullableOrEmpty(visibleSocialLinks)) &&
        searchSectionIsVisible;

    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);

    return (
        <div
            className={style(classes.root, {
                withCover: hasCover,
                hasProfileImage: hasProfileImage,
                searchSectionIsVisible: searchSectionIsVisible,
                fullScreen: !fullScreenToggledActionsSet.isEmpty(),
                hideFooterBanner: contextProps.viewerHideFooterBanner || removeHoppBanner || (autoRedirectNotificationIsShown && showAutoRedirectNotificationInTheBottom),
                withSkipHeader: !!targetUrl,
                stickyPreRollHeader: normalizedPreRollExperience === PreRollExperience.StickyHeader
            })}
        >
            {!hasCover ? (
                <DoppePageBackgroundView
                    className={style(classes.pageBackground, {})}
                />
            ) : null}
            <FloatingWindowOpener
                ref={autoRedirectNotificationOpenerRef}
                onOpen={() => {
                    setAutoRedirectNotificationIsShown(true);
                }}
                onClose={() => {
                    setAutoRedirectNotificationIsShown(false);
                }}
            >
                {(renderProps) => {

                    const {
                        additionalMessage
                    } = renderProps.contextProps;

                    return (
                        <InnerWindowAnimator
                            windowClosingControllerRef={renderProps.windowClosingControllerRef}
                            openTransition={{
                                animation: [
                                    {transform: showAutoRedirectNotificationInTheBottom ? 'translate(0, 100%)' : 'translate(0, -100%)'},
                                    {transform: 'translate(0, 0)'},
                                ],
                                durationMilliseconds: 200
                            }}
                            closeTransition={{
                                animation: [
                                    {transform: 'translate(0, 0)'},
                                    {transform: showAutoRedirectNotificationInTheBottom ? 'translate(0, 100%)' : 'translate(0, -100%)'},
                                ],
                                durationMilliseconds: 200
                            }}
                        >
                            <div
                                className={style(classes.autoRedirectNotificationContainer, {showOnPageBottom: showAutoRedirectNotificationInTheBottom})}
                            >
                                <DoppeViewerAutoRedirectNotificationContent
                                    showCreatedByHopp={!additionalMessage && showAutoRedirectNotificationInTheBottom}
                                    className={style(classes.autoRedirectNotification, classes.floatingViewShadow)}
                                    {...renderProps.contextProps}
                                />
                            </div>
                        </InnerWindowAnimator>
                    )
                }}
            </FloatingWindowOpener>
            <FloatingWindowOpener
                ref={defaultNotificationOpenerRef}
                onOpen={() => {
                    setDefaultNotificationIsShown(true);
                }}
                onClose={() => {
                    setDefaultNotificationIsShown(false);
                }}
            >
                {(renderProps) => {

                    return (
                        <InnerWindowAnimator
                            windowClosingControllerRef={renderProps.windowClosingControllerRef}
                            openTransition={{
                                animation: [
                                    {transform: 'translate(0, -100%)'},
                                    {transform: 'translate(0, 0)'},
                                ],
                                durationMilliseconds: 200
                            }}
                            closeTransition={{
                                animation: [
                                    {transform: 'translate(0, 0)'},
                                    {transform: 'translate(0, -100%)'},
                                ],
                                durationMilliseconds: 200
                            }}
                        >
                            <div
                                className={style(classes.defaultNotificationContainer, {})}
                            >
                                <DoppeViewerDefaultNotificationContent
                                    className={style(classes.defaultNotification, classes.floatingViewShadow)}
                                    {...renderProps.contextProps}
                                />
                            </div>
                        </InnerWindowAnimator>
                    )
                }}
            </FloatingWindowOpener>
            <div
                className={style(classes.main)}
            >
                <DelayedComponentRenderer
                    delayMilliseconds={
                        skipHeaderIsFloating && pageViewerContext.mode !== BymoPageViewerMode.Preview ?
                            1000 : 0
                    }
                >
                    <DomElementAnimator
                        autoAnimateOnMount={skipHeaderIsFloating ? {
                            animation: [
                                {transform: 'translateY(-20px)', opacity: 0},
                                {transform: 'translateY(0)', opacity: 1},
                            ],
                            durationMilliseconds: 100
                        } : undefined}
                    >
                        <BymoSkipHeader
                            className={style(classes.skipHeader, {})}
                            autoRedirectSeconds={null}
                            skin={evaluateFunction(() => {

                                if (bymoPageLayout === BymoPageLayout.Wide) {
                                    return BymoSkipHeaderSkin.Standard;
                                }

                                switch (normalizedPreRollExperience) {
                                    case PreRollExperience.FloatingHeader:
                                        return BymoSkipHeaderSkin.Round;
                                    default:
                                        return BymoSkipHeaderSkin.Standard
                                }
                            })}
                        />
                    </DomElementAnimator>
                </DelayedComponentRenderer>
                {hasCover ? (
                    <div
                        className={style(classes.coverBackground, {}, FlexLayout.column.default)}
                    >
                        <DoppeViewerCoverView
                            className={style(classes.coverImage)}
                            fluidOnDesktop={true}
                        />
                        <DoppePageBackgroundView
                            className={style(classes.pageWithCoverBackgroundView, {})}
                        />

                    </div>
                ) : null}
                <div
                    className={style(classes.content, FlexLayout.column.default)}
                >
                    <div
                        className={style(classes.profileView, FlexLayout.column.alignToCenter)}
                    >
                        <DoppeHideableValueRenderer
                            hideableValue={profilePictureLinkInBio}
                            renderFunction={profilePicture => (
                                <DoppePageProfilePicture
                                    className={style(classes.profileImage)}
                                    profilePicture={profilePicture}
                                    size={100}
                                />
                            )}
                        />
                        <DoppePageProfileNameView
                            className={style(classes.profileName, {}, FlexLayout.verticallyJustifyToEnd)}
                            defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                        />
                        {stringIsNotNullableOrWhiteSpace(bioTextVisibleValue) ? evaluateFunction(() => {

                            const maxVisibleLines =
                                doppeHideableValueGetVisibleValue(bioTextMaxLines, Infinity);

                            if (maxVisibleLines === Infinity) {
                                return (
                                    <div
                                        className={style(classes.bioText, {})}
                                    >
                                        {bioTextVisibleValue}
                                    </div>
                                )
                            } else if (maxVisibleLines > 1) {
                                return (
                                    <MultilineExpandableText
                                        className={style(classes.bioTextMultiline, {})}
                                        text={bioTextVisibleValue}
                                        expandButtonText={viewerLang.seeMore}
                                        lineHeight={'18px'}
                                        maxLines={maxVisibleLines}
                                        collapseOnTextChange={true}
                                    />
                                )
                            } else {
                                return (
                                    <ExpandableText
                                        className={style(classes.bioText, {})}
                                        seeMoreButtonContent={(
                                            <div className={style(classes.seeMoreButton, {})}>{viewerLang.seeMore}</div>
                                        )}
                                    >
                                        {bioTextVisibleValue}
                                    </ExpandableText>
                                )
                            }
                        }) : null}
                        {arrayIsNotNullableOrEmpty(visibleSocialLinks) ? (
                            <DoppePageSocialLinks
                                className={style(classes.socialLinks, {})}
                                socialLinks={visibleSocialLinks}
                            />
                        ) : null}
                        {showProfileSeparator ? (
                            <div
                                className={style(classes.profileSeparator, {})}
                            />
                        ) : null}
                    </div>
                    <DoppLayoutContentTitle
                        text={pageTitle}
                    />
                    {searchSectionIsVisible ? (
                        <>
                            <DoppeHideableValueRenderer
                                hideableValue={callToActionText}
                                renderFunction={callToActionText => {

                                    const normalizedCallToActionText = callToActionText.trim();

                                    return normalizedCallToActionText !== '' ? (
                                        <div
                                            className={style(classes.supportText, FlexLayout.centerContent)}
                                        >
                                            {textNormalizeForHtmlDisplay(normalizedCallToActionText, 2)}
                                        </div>
                                    ) : null
                                }}
                            />
                            <div
                                className={style(classes.codeWidget, {})}
                            >
                                <DoppeViewerAction
                                    action={{
                                        ...doppeDtoActionGetDefaultProps(),
                                        viewType: DoppeDtoActionViewType.InPage,
                                        settings: {
                                            autoCompleteBehaviour: BymoTemplateSpeedDialAutoComplete.OnInputFocus,
                                            showFullScreenAutoCompleteOnMobile: true,
                                            searchPlaceholder: searchPlaceholder
                                        },
                                        actionTypeId: doppeActionTypeSpeedDial.id,
                                        callToActionDescription: doppeHideableValueCreateHidden(''),
                                        enabled: true,
                                        id: 'SPEED_DIAL',
                                        name: `Enter a Code`
                                    }}
                                    renderActionView={actionViewRenderFunction}
                                    fullScreenModeTogglingController={fullScreenModeTogglingController}
                                />
                            </div>
                        </>
                    ) : null}
                    {actionsListAppearance !== BymoTemplateSpeedDialActionsListAppearance.Hidden && iterableAny(actions, action => action.enabled) ? (
                        <>
                            {
                                searchSectionIsVisible ?
                                    <div
                                        className={style(classes.actionsSeparator, {})}
                                    />
                                    :
                                    null
                            }
                            {stringIsNotNullableOrWhiteSpace(actionsHeadingVisibleValue) ? (
                                <div
                                    className={style(classes.supportText, {})}>{textNormalizeForHtmlDisplay(actionsHeadingVisibleValue.trim(), 2)}</div>
                            ) : null}
                            <DoppeViewerActionsList
                                className={style(classes.actionsList, {})}
                                supportsRenderingFramelessWidgets={true}
                                renderActionView={actionViewRenderFunction}
                                fullScreenModeTogglingController={fullScreenModeTogglingController}
                            />
                        </>
                    ) : null}
                </div>
                <DoppePageCookieBannerOpener
                    className={style(classes.cookieBanner, {})}
                />
                <DoppePageFooterBanner
                    className={style(classes.contentFooter, {})}
                    bannerMessage={viewerLang.claim}
                    targetUrl={`https://www.hopp.co`}
                />
            </div>
        </div>
    )

})
